import InputText from 'primevue/inputtext'
import Select from 'primevue/select'
import SyncUpField from '@dashboard/components/common/SyncUpField.vue'
import RichTextEditor from '@dashboard/components/common/RichTextEditor.vue'
import Textarea from 'primevue/textarea'
import OfferFieldRadioButtons from '@dashboard/modules/offer/components/OfferFieldRadioButtons.vue'
import { shellIcons } from '@dashboard/components/icons/shellIcons'

import countries from '@dashboard/data/countries'
import { computed } from 'vue'

const settingLabel = (options: { placeholder?: string; rich?: boolean }) => ({
  field: 'label',
  label: 'Label',
  component: {
    type: options.rich ? RichTextEditor : InputText,
    props: {
      placeholder: options.placeholder || 'New Form Field',
      size: 'large',
      class: 'w-full'
    }
  },
  ...options
})

const settingPlaceholder = (options: { placeholder?: string }) => ({
  field: 'placeholder',
  label: 'Placeholder',
  component: {
    type: InputText,
    props: {
      size: 'large',
      class: 'w-full'
    }
  },
  ...options
})

const settingSync = (options: object) => ({
  field: 'espField',
  label: null,
  component: {
    type: SyncUpField,
    props: { label: '$FIELD To Sync To' }
  },
  ...options
})

const settingValue = (options: object) => ({
  field: 'value',
  label: 'Value',
  component: {
    type: InputText,
    props: {
      size: 'large',
      class: 'w-full'
    }
  },
  ...options
})

const settingRichText = (options: { multiline?: boolean; field?: string; label?: string }) => ({
  field: 'options.content',
  label: 'Content',
  component: {
    type: RichTextEditor,
    props: {
      class: 'w-full',
      multiline: options.multiline !== false
    }
  },
  ...options
})

const settingHtml = (options: object) => ({
  field: 'options.content',
  label: 'HTML Code',
  component: {
    type: Textarea,
    props: {
      class: 'w-full h-24'
    }
  },
  ...options
})

/* FIELDS */

const fieldText = {
  icon: 'lucide:type',
  id: 'text',
  label: 'Text',
  fields: computed(() => [settingLabel({ placeholder: 'New Form Field' }), settingPlaceholder({}), settingSync({})])
}

const fieldQuestion = {
  icon: shellIcons.questions,
  id: 'sg',
  label: 'Question',
  fields: computed(() => [settingLabel({ placeholder: 'Tell me about...' })])
}

const fieldHidden = {
  icon: 'lucide:eye-off',
  id: 'hidden',
  label: 'Hidden Field',
  fields: computed(() => [settingSync({}), settingValue({})])
}

const fieldCheckbox = {
  icon: 'lucide:check',
  id: 'checkbox',
  label: 'Checkbox',
  fields: computed(() => [
    settingRichText({ field: 'label', label: 'Label', multiline: false }),
    settingSync({}),
    settingValue({})
  ])
}

const fieldTextarea = {
  icon: 'lucide:edit',
  id: 'textarea',
  label: 'Text Area',
  fields: computed(() => [settingLabel({ placeholder: 'Tell me about...' }), settingPlaceholder({}), settingSync({})])
}

const fieldPhone = {
  icon: 'lucide:phone',
  id: 'phone',
  label: 'Phone',
  fields: computed(() => [
    settingLabel({ placeholder: 'Phone number' }),
    settingPlaceholder({}),
    {
      field: 'options.defaultCountry',
      label: 'Fallback Country (after visitor location)',
      component: {
        type: Select,
        props: {
          options: countries,
          optionLabel: 'label',
          optionValue: 'value',
          class: 'w-full'
        }
      }
    },
    {
      field: 'options.errorMessage',
      label: 'Inline Invalid Number Message',
      component: {
        type: InputText,
        props: {
          size: 'large',
          placeholder: 'Please enter a valid phone number.',
          class: 'w-full'
        }
      }
    },
    settingSync({})
  ])
}

const fieldNumber = {}

const fieldRadioButton = (field: { options: { availableOptions?: { name: string; id: string | number }[] } }) => ({
  icon: 'lucide:list',
  id: 'radio',
  label: 'Radio Buttons',
  fields: computed(() => [
    settingLabel({ placeholder: 'New Form Field', rich: true }),
    {
      field: 'options.availableOptions',
      label: 'Available Options',
      component: {
        type: OfferFieldRadioButtons,
        props: {
          options: field.options.availableOptions
        },
        events: {
          addOption: () => {
            if (!field.options.availableOptions) {
              field.options.availableOptions = []
            }
            field.options.availableOptions.push({
              name: `Option ${field.options.availableOptions.length + 1}`,
              id: field.options.availableOptions.length + 1
            })
          },
          'update:option': (option: any, index: number) => {
            field.options.availableOptions![index] = option
          },
          deleteOption: (_: any, index: number) => {
            field.options.availableOptions!.splice(index, 1)
          }
        }
      }
    },
    settingSync({})
  ])
})

const fieldSpacer = {
  icon: 'lucide:columns',
  id: 'spacer',
  label: 'Spacer',
  fields: computed(() => [])
}

const fieldRichText = {
  icon: 'lucide:heading-1',
  id: 'richtext',
  label: 'Rich Text Block',
  fields: computed(() => [
    settingRichText({}),
    {
      field: 'options.textSize',
      label: 'Text Size',
      component: {
        type: Select,
        props: {
          options: [
            { label: 'Extra Small', value: '0.5' },
            { label: 'Small', value: '0.75' },
            { label: 'Medium', value: '1' },
            { label: 'Large', value: '1.25' }
          ],
          optionLabel: 'label',
          optionValue: 'value',
          placeholder: 'Default: Medium',
          class: 'w-full'
        }
      }
    }
  ])
}

const fieldHtml = {
  icon: 'lucide:code',
  id: 'html',
  label: 'HTML Block',
  fields: computed(() => [settingHtml({})])
}

const fieldEmail = {
  icon: 'lucide:mail',
  internal: true,
  id: 'email',
  label: 'Email',
  fields: computed(() => [settingLabel({ placeholder: 'Email address' }), settingPlaceholder({})])
}

const fieldSubmit = {
  icon: 'lucide:send-horizontal',
  internal: true,
  id: 'submit',
  label: 'Submit Button',
  fields: computed(() => [])
}

// Canonical JS source for default form fields — the ONLY place this is defined
// on the frontend. Both offerStore.create() and the Fields tab's first insertion
// import from here; neither has an inline definition.
//
// The PHP counterpart lives in OfferFormApiService::defaultFormFields() and must
// be kept in sync manually. PHP cannot import TypeScript files, so the two
// definitions are intentionally separate across the language boundary. When
// adding or removing a key here, update the PHP method at the same time.
export const defaultFormFields = () => [
  {
    label: 'Email address',
    placeholder: 'Your email address',
    displayType: 'email',
    width: 6,
    options: {}
  },
  {
    displayType: 'submit',
    width: 6,
    options: {}
  }
]

export const availableFields = computed(() => [
  fieldText,
  fieldQuestion,
  fieldHidden,
  fieldCheckbox,
  fieldTextarea,
  fieldPhone,
  // fieldNumber,
  fieldRadioButton,
  fieldSpacer,
  fieldRichText,
  fieldHtml,
  fieldEmail,
  fieldSubmit
])
